import React from 'react';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus } from '../../types/wizard';
import { usePageTracking, useAnalytics } from '../../hooks/useAnalytics';

interface ApprovedProps {
  applicationStatus: ApplicationStatus;
}

const Approved: React.FC<ApprovedProps> = ({ applicationStatus }) => {
  const wasNotApproved = applicationStatus.applicationStatus === 'NOT_APPROVED'
    || applicationStatus.applicantStep === 'Rejected';
  const firstName = applicationStatus.name?.trim().split(/\s+/)[0];
  usePageTracking('Approved');
  const { trackOnboardingComplete } = useAnalytics();

  React.useEffect(() => {
    if (wasNotApproved) return;
    trackOnboardingComplete({
      totalTime: 0,
      stepsCompleted: 7,
      completionDate: new Date().toISOString(),
    });
  }, [trackOnboardingComplete, wasNotApproved]);

  if (wasNotApproved) {
    const reapplyDate = applicationStatus.reapplyEligibleAt
      ? new Date(applicationStatus.reapplyEligibleAt).toLocaleDateString('en-CA', {
          year: 'numeric', month: 'long', day: 'numeric'
        })
      : null;

    return (
      <WizardContainer currentStepId="approved" applicationStatus={applicationStatus}>
        <div className="max-w-2xl mx-auto">
          <div className="card p-8 text-center">
            <div className="text-6xl mb-6">📋</div>
            <h1 className="text-3xl font-bold text-charcoal mb-4">Application Update</h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for your interest in delivering with Valley Eats. We are unable to approve your application at this time.
            </p>
            {reapplyDate && (
              <div className="soft-panel p-5">
                <p className="text-charcoal">You may submit a new application after <strong>{reapplyDate}</strong>.</p>
              </div>
            )}
            <p className="mt-6 text-sm text-gray-600">
              Questions? Email <a href="mailto:relations@valleyeats.ca" className="text-logo-green hover:underline">relations@valleyeats.ca</a>.
            </p>
          </div>
        </div>
      </WizardContainer>
    );
  }

  return (
    <WizardContainer
      currentStepId="approved"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <div className="text-6xl mb-6">🎉</div>

          <h1 className="text-4xl font-bold text-logo-green mb-4">
            Congratulations!
          </h1>

          <p className="text-2xl text-charcoal mb-6">
            Welcome to the Valley Eats team{firstName ? `, ${firstName}` : ''}!
          </p>

          <div className="bg-logo-green/10 border-2 border-logo-green rounded-lg p-6 mb-6">
            <p className="text-lg text-charcoal mb-4">
              You're now approved to start delivering with Valley Eats in <strong>{applicationStatus.deliveryArea}</strong>!
            </p>
          </div>

          <div className="soft-panel p-6 text-left mb-6">
            <h2 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
              <span>🚀</span>
              <span>Next Steps to Get Started</span>
            </h2>
            <ol className="text-sm text-gray-600 space-y-3">
              <li className="flex items-start gap-2">
                <span className="font-bold text-logo-green">1.</span>
                <span><strong>Download the Valley Eats Driver App</strong> from the App Store or Google Play</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-logo-green">2.</span>
                <span><strong>Log in</strong> using the credentials we've sent to your email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-logo-green">3.</span>
                <span><strong>Complete your profile</strong> and set your availability</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-logo-green">4.</span>
                <span><strong>Go online</strong> and start accepting deliveries!</span>
              </li>
            </ol>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-left">
              <div className="text-2xl mb-2">📱</div>
              <h3 className="font-semibold text-charcoal mb-1">Driver App</h3>
              <p className="text-sm text-gray-600">
                Check your email for download links and login instructions
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-left">
              <div className="text-2xl mb-2">💬</div>
              <h3 className="font-semibold text-charcoal mb-1">Support</h3>
              <p className="text-sm text-gray-600">
                Questions? Email us at{' '}
                <a href="mailto:relations@valleyeats.ca" className="text-logo-green hover:underline">
                  relations@valleyeats.ca
                </a>
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>📧 Check your email</strong> - We've sent you important information including your driver login credentials, tax forms, and getting started guide.
            </p>
          </div>
        </div>
      </div>
    </WizardContainer>
  );
};

export default Approved;
